"use client";

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useRouter } from 'next/navigation';
import { SidebarNav } from '@/components/layout/sidebar';
import { FileBrowser, type ShareBrowseContext } from '@/components/browser/file-browser';
import { UsersPage } from '@/components/users/users-page';
import { RolesPage } from '@/components/roles/roles-page';
import { TrashPage } from '@/components/trash/trash-page';
import { SharedWithMePage } from '@/components/sharing/shared-with-me-page';
import { LicensesPage } from '@/components/licenses/licenses-page';
import { CorrespondencePage } from '@/components/correspondence/correspondence-page';
import { SettingsPage } from '@/components/settings/settings-page';
import { Loader2 } from 'lucide-react';
import { dmsApi } from '@/lib/api';
import { toast } from '@/hooks/use-toast';

function Dashboard() {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const [activeFolderId, setActiveFolderId] = useState<string | undefined>();
  const [activeView, setActiveView] = useState<
    'all' | 'trash' | 'shared' | 'licenses' | 'correspondence' | 'users' | 'roles' | 'settings'
  >('all');
  const [shareBrowseContext, setShareBrowseContext] = useState<ShareBrowseContext | null>(null);
  const [openSharedFile, setOpenSharedFile] = useState<{
    fileId: string;
    permissionLevel: 'view' | 'edit';
  } | null>(null);

  const handleOpenSharedFileDone = useCallback(() => setOpenSharedFile(null), []);

  const handleSelectView = useCallback(
    (v: 'all' | 'trash' | 'shared' | 'licenses' | 'correspondence' | 'users' | 'roles' | 'settings') => {
      setShareBrowseContext(null);
      setOpenSharedFile(null);
      setActiveView(v);
    },
    []
  );

  const [activeDepartmentId, setActiveDepartmentId] = useState<string | undefined>();
  const [activeDepartmentName, setActiveDepartmentName] = useState<string | undefined>();
  const [qrTransactionId, setQrTransactionId] = useState<string | undefined>();
  const [qrOpenFileId, setQrOpenFileId] = useState<string | undefined>();

  const handleSelectDepartment = useCallback((departmentId: string, departmentName?: string) => {
    setActiveDepartmentId(departmentId);
    setActiveDepartmentName(departmentName);
  }, []);

  const handleSelectFolder = useCallback((id: string) => {
    setShareBrowseContext(null);
    setOpenSharedFile(null);
    setActiveFolderId(id);
  }, []);

  useEffect(() => {
    if (!isLoading && !user) {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      const returnTo = search ? `/${search}` : '/';
      router.push(`/login?next=${encodeURIComponent(returnTo)}`);
    }
  }, [isLoading, user, router]);

  /** Open a transaction (and its file) directly from a scanned correspondence QR code. */
  useEffect(() => {
    if (isLoading || !user || typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('view') !== 'correspondence') return;
    const transactionId = params.get('transaction');
    const token = params.get('t');
    if (!transactionId || !token) return;

    let cancelled = false;
    (async () => {
      try {
        const resolved = await dmsApi.correspondence.resolveQr(transactionId, token);
        if (cancelled || !resolved?.departmentId) return;

        let name: string | undefined;
        try {
          const dept = await dmsApi.departments.get(resolved.departmentId);
          name = dept?.name;
        } catch {
          name = undefined;
        }
        if (cancelled) return;

        const fileId =
          resolved.transaction?.dmsFileId
            ? String(resolved.transaction.dmsFileId)
            : resolved.transaction?.attachments?.[0]?.dmsFileId
              ? String(resolved.transaction.attachments[0].dmsFileId)
              : undefined;

        setActiveDepartmentId(resolved.departmentId);
        setActiveDepartmentName(name);
        setQrTransactionId(transactionId);
        setQrOpenFileId(fileId);
        setActiveView('correspondence');
      } catch (err: any) {
        toast({
          variant: 'destructive',
          title: 'Could not open scanned transaction',
          description: err?.error || err?.message || 'The QR code may be invalid or expired.',
        });
      } finally {
        if (!cancelled) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, user]);

  if (isLoading || !user) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center bg-background">
        <Loader2 className="animate-spin text-primary w-12 h-12 mb-4" />
        <p className="text-lg font-medium text-muted-foreground">Authenticating CoopXL Docs...</p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeView) {
      case 'all':
        return (
          <FileBrowser
            initialFolderId={activeFolderId}
            departmentId={activeDepartmentId}
            viewMode="all"
            shareBrowseContext={shareBrowseContext}
            openSharedFile={openSharedFile}
            onOpenSharedFileDone={handleOpenSharedFileDone}
            onExitSharedBrowse={() => handleSelectView('shared')}
          />
        );
      case 'trash':
        return <TrashPage />;
      case 'shared':
        return (
          <SharedWithMePage
            onOpenSharedFolder={(folderId, ctx) => {
              setActiveFolderId(folderId);
              setShareBrowseContext(ctx);
              setActiveView('all');
            }}
            onOpenSharedFile={(fileId, permissionLevel) => {
              setOpenSharedFile({ fileId, permissionLevel });
              setActiveView('all');
            }}
          />
        );
      case 'licenses':
        return (
          <LicensesPage
            departmentId={activeDepartmentId}
            departmentName={activeDepartmentName}
          />
        );
      case 'correspondence':
        return (
          <CorrespondencePage
            departmentId={activeDepartmentId}
            departmentName={activeDepartmentName}
            initialTransactionId={qrTransactionId}
            initialOpenFileId={qrOpenFileId}
            onInitialTransactionHandled={() => {
              setQrTransactionId(undefined);
              setQrOpenFileId(undefined);
            }}
          />
        );
      case 'users':
        return <UsersPage />;
      case 'roles':
        return <RolesPage />;
      case 'settings':
        return (
          <SettingsPage
            departmentId={activeDepartmentId}
            departmentName={activeDepartmentName}
          />
        );
      default:
        return (
          <FileBrowser
            initialFolderId={activeFolderId}
            shareBrowseContext={shareBrowseContext}
            openSharedFile={openSharedFile}
            onOpenSharedFileDone={handleOpenSharedFileDone}
            onExitSharedBrowse={() => handleSelectView('shared')}
          />
        );
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <SidebarNav
        onSelectFolder={handleSelectFolder}
        onSelectDepartment={handleSelectDepartment}
        activeView={activeView}
        onSelectView={handleSelectView}
      />
      <main className="flex-1 overflow-hidden">
        {renderContent()}
      </main>
    </div>
  );
}

export default function RootPage() {
  return <Dashboard />;
}
